/** Ranch: the places, supplies, equipment, people, money and reports that support the cattle. */
import { Suspense, useState } from "react";
import { Link, useParams } from "react-router";
import { CONTACT_ROLES, STOCK_KIND, PLACE_KIND, ASSET_KIND, type AssetSummary, type ContactSummary, type PlaceSummary, type RanchSectionDef, type StockItemSummary, type StockLotDTO, type AnimalSummary, type Fact } from "../../../contract";
import { formatDate, formatQuantity, formatNumber } from "../../lib/format";
import { plural, t, useLanguage } from "../../lib/i18n";
import { useApi } from "../../lib/query";
import { useCan, useMe } from "../../lib/session";
import { openEntity, openRecord } from "../../forms/EventForm";
import { entitySections, eventTypesFor, ranchSectionComponent, registry } from "../../registry";
import { Spinner, Badges, Button, Card, Chip, Empty, ErrorNote, Facts, Icon, List, Loading, Menu, PageHeader, Row, Section, Tabs, TextInput, useFromHere } from "../../ui/kit";
import { useCaptureSubject } from "../Composer";
import { Page } from "../Layout";
import { Timeline } from "../Timeline";
import { AnimalRowView } from "./Cattle";
import { NotFound } from "./NotFound";

const missing = (err: unknown) => (err as { status?: number } | null)?.status === 404;

const CORE_SECTIONS: Array<RanchSectionDef & { to: string }> = [
  { id: "places", label: "Places", description: "Pastures, pens, traps and facilities, with who's where.", icon: "place", group: "places", keywords: ["pasture", "pen"], to: "/ranch/places" },
  { id: "supplies", label: "Supplies", description: "Medicine, vaccine, feed, hay, mineral and parts on hand.", icon: "stock", group: "supplies", keywords: ["inventory", "medicine", "feed"], to: "/ranch/supplies" },
  { id: "equipment", label: "Equipment", description: "Trucks, tractors, chutes, windmills and their upkeep.", icon: "tractor", group: "equipment", keywords: ["truck", "tractor"], to: "/ranch/equipment" },
  { id: "people", label: "People and businesses", description: "Vets, buyers, suppliers, owners and associations.", icon: "contact", group: "people", keywords: ["vet", "buyer", "supplier"], to: "/ranch/people" },
];

const GROUP_ORDER = ["places", "supplies", "equipment", "people", "money", "reports", "data"] as const;

export function RanchHome() {
  useLanguage();
  const can = useCan();
  const moduleSections = registry.ranchSections.filter((s) => !s.permission || can(s.permission)).map((s) => ({ ...s, to: `/ranch/${s.id}` }));
  const all = [...CORE_SECTIONS, ...moduleSections].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
  return (
    <Page>
      <PageHeader title={t("Ranch")} subtitle={t("Places, supplies, equipment, people, money and reports.")} />
      <div className="tiles">
        {all.map((s) => (
          <Link key={s.id} to={s.to} className="tile">
            <span className="tile-icon"><Icon name={s.icon} size={22} /></span>
            <span className="tile-title">{t(s.label)}</span>
            <span className="tile-body">{t(s.description)}</span>
          </Link>
        ))}
        <Link to="/settings" className="tile">
          <span className="tile-icon"><Icon name="settings" size={22} /></span>
          <span className="tile-title">{t("Settings and crew")}</span>
          <span className="tile-body">{t("Crew access, ranch words, language, units and this device.")}</span>
        </Link>
      </div>
    </Page>
  );
}

export function RanchSectionRoute() {
  const { section } = useParams();
  const Component = section ? ranchSectionComponent(section) : undefined;
  const def = registry.ranchSections.find((s) => s.id === section);
  if (!def || !Component) return <NotFound />;
  return <Component />;
}

// ---------------------------------------------------------------- places

export function PlacesPage() {
  const places = useApi<{ items: PlaceSummary[] }>("/places");
  const { data: me } = useMe();
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Places")} actions={<Button icon="plus" onClick={() => openEntity({ kind: "place" })}>{t("Add place")}</Button>} />
      {places.isLoading ? <Loading /> : !places.data?.items.length ? <Empty icon="place" title={t("No places yet")} body={t("Add pastures and pens, or say “Moved the heifers to the Creek trap” and Hondo will offer to add it.")} /> : (
        <List>
          {places.data.items.map((p) => (
            <Row key={p.id} icon="place" tone="land" to={`/ranch/places/${p.id}`} title={p.name}
              meta={[t(PLACE_KIND[p.kind as keyof typeof PLACE_KIND] ?? p.kind), plural(p.head, "{{count}} head", "{{count}} head"), p.area ? formatQuantity(p.area, me?.ranch?.units) : null, p.capacity ? t("holds {{count}}", { count: p.capacity }) : null].filter(Boolean).join(" · ")}
              trailing={<Badges badges={p.badges} max={2} />} />
          ))}
        </List>
      )}
    </Page>
  );
}

export function PlacePage() {
  const { id } = useParams();
  const p = useApi<PlaceSummary & { animals: AnimalSummary[]; facts: Fact[]; attrs: Record<string, unknown> }>(`/places/${id}`);
  useCaptureSubject(p.data ? { place_id: p.data.id, subject: { kind: "place", id: p.data.id, label: p.data.name } } : null);
  const [tab, setTab] = useState("cattle");
  const from = useFromHere(p.data?.name ?? t("Place"));
  const sections = entitySections("place").filter((s) => !p.data || !s.show || s.show(p.data));
  const types = eventTypesFor("place");
  if (p.isLoading) return <Loading />;
  if (!p.data) return missing(p.error) ? <NotFound /> : <Page><ErrorNote error={p.error} retry={() => p.refetch()} /></Page>;
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/places", label: t("Places") }} title={p.data.name} subtitle={[t(PLACE_KIND[p.data.kind as keyof typeof PLACE_KIND] ?? p.data.kind), plural(p.data.animals.length, "{{count}} head here", "{{count}} head here")].join(" · ")}
        actions={<>
          {types.length > 0 && <Menu label={t("Add a record")} variant="secondary" items={types.map((e) => ({ label: t(e.label), icon: e.icon, onSelect: () => openRecord({ initial: { type: e.type, subjects: [{ kind: "place", id: p.data!.id, role: e.subjects.find((s) => s.kind === "place")!.role, label: p.data!.name }] } }) }))}>{t("Add a record")}</Menu>}
          <Button icon="edit" onClick={() => openEntity({ kind: "place", id: p.data!.id, initial: { name: p.data!.name, kind: p.data!.kind } })}>{t("Edit")}</Button>
        </>} />
      {p.data.facts.length > 0 && <Card><Facts facts={p.data.facts} /></Card>}
      <Tabs value={tab} onChange={setTab} tabs={[{ id: "cattle", label: t("Cattle here"), count: p.data.animals.length }, { id: "history", label: t("History") }, ...sections.map((s) => ({ id: s.id, label: t(s.label) }))]} />
      {tab === "cattle" && (p.data.animals.length ? <ul className="animal-list">{p.data.animals.map((a) => <AnimalRowView key={a.id} a={a} state={from} />)}</ul> : <p className="muted">{t("No cattle here right now.")}</p>)}
      {tab === "history" && <Timeline subject={`place:${p.data.id}`} />}
      {sections.filter((s) => s.id === tab).map((s) => <Suspense key={s.id} fallback={<Spinner />}><s.Component entity={p.data!} /></Suspense>)}
    </Page>
  );
}

// ---------------------------------------------------------------- supplies

export function SuppliesPage() {
  const items = useApi<{ items: StockItemSummary[] }>("/stock-items");
  const [q, setQ] = useState("");
  const shown = (items.data?.items ?? []).filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));
  const kinds = [...new Set(shown.map((i) => i.kind))];
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Supplies")} actions={<Button icon="plus" onClick={() => openEntity({ kind: "stock_item" })}>{t("Add product")}</Button>} />
      <div className="search-input"><Icon name="search" size={18} /><TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Find a product")} aria-label={t("Find a product")} /></div>
      {items.isLoading ? <Loading /> : !shown.length ? <Empty icon="stock" title={t("No supplies yet")} body={t("Say “Bought 10 bottles of LA-200, lot 4471” or photograph a receipt.")} /> : kinds.map((k) => (
        <Section key={k} title={t(STOCK_KIND[k as keyof typeof STOCK_KIND] ?? k)}>
          <List>{shown.filter((i) => i.kind === k).map((i) => (
            <Row key={i.id} icon="stock" to={`/ranch/supplies/${i.id}`} title={i.name}
              meta={t("{{amount}} on hand", { amount: `${formatNumber(i.on_hand)} ${t(i.unit)}` })}
              trailing={i.reorder_at !== null && i.on_hand <= i.reorder_at ? <Chip tone="warn" icon="warn">{t("Low")}</Chip> : undefined} />
          ))}</List>
        </Section>
      ))}
    </Page>
  );
}

export function StockPage() {
  const { id } = useParams();
  const s = useApi<StockItemSummary & { lots: StockLotDTO[]; moves: Array<{ id: string; delta: number; unit: string; reason: string; occurred_at: string; event_id: string | null; note: string | null }> }>(`/stock-items/${id}`);
  const sections = entitySections("stock").filter((x) => !s.data || !x.show || x.show(s.data));
  if (s.isLoading) return <Loading />;
  if (!s.data) return missing(s.error) ? <NotFound /> : <Page><ErrorNote error={s.error} retry={() => s.refetch()} /></Page>;
  const reasons: Record<string, string> = { receive: "Received", use: "Used", adjust: "Adjusted", transfer: "Moved", sell: "Sold", waste: "Thrown out", count: "Counted" };
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/supplies", label: t("Supplies") }} eyebrow={t(STOCK_KIND[s.data.kind as keyof typeof STOCK_KIND] ?? s.data.kind)} title={s.data.name}
        subtitle={t("{{amount}} on hand", { amount: `${formatNumber(s.data.on_hand)} ${t(s.data.unit)}` })}
        actions={<Button icon="plus" onClick={() => openEntity({ kind: "stock_lot", initial: { item_id: s.data!.id } })}>{t("Receive stock")}</Button>} />
      {sections.map((x) => <Section key={x.id} title={t(x.label)}><Suspense fallback={<Spinner />}><x.Component entity={s.data!} /></Suspense></Section>)}
      <Section title={t("Lots")}>
        {s.data.lots.length ? <List>{s.data.lots.map((l) => <Row key={l.id} icon="tag" title={l.code ?? t("No lot number")} meta={[`${formatNumber(l.on_hand)} ${t(s.data!.unit)}`, l.expires_at ? t("expires {{date}}", { date: formatDate(l.expires_at) }) : null, l.place?.label].filter(Boolean).join(" · ")} />)}</List> : <p className="muted">{t("No lots recorded.")}</p>}
      </Section>
      <Section title={t("Ins and outs")}>
        <List>{s.data.moves.map((m) => <Row key={m.id} icon={m.delta > 0 ? "plus" : "minus"} title={`${t(reasons[m.reason] ?? m.reason)} ${m.delta > 0 ? "+" : ""}${formatNumber(m.delta)} ${t(m.unit)}`} meta={[formatDate(m.occurred_at), m.note].filter(Boolean).join(" · ")} to={m.event_id ? `/history/${m.event_id}` : undefined} />)}</List>
      </Section>
    </Page>
  );
}

// ---------------------------------------------------------------- equipment and people

export function EquipmentPage() {
  const assets = useApi<{ items: AssetSummary[] }>("/assets");
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Equipment")} actions={<Button icon="plus" onClick={() => openEntity({ kind: "asset" })}>{t("Add equipment")}</Button>} />
      {assets.isLoading ? <Loading /> : !assets.data?.items.length ? <Empty icon="tractor" title={t("No equipment yet")} /> : (
        <List>{assets.data.items.map((a) => <Row key={a.id} icon="wrench" to={`/ranch/equipment/${a.id}`} title={a.name} meta={[t(ASSET_KIND[a.kind as keyof typeof ASSET_KIND] ?? a.kind), [a.make, a.model].filter(Boolean).join(" "), a.place?.label].filter(Boolean).join(" · ")} />)}</List>
      )}
    </Page>
  );
}

export function AssetPage() {
  const { id } = useParams();
  const a = useApi<Record<string, any>>(`/assets/${id}`);
  useCaptureSubject(a.data ? { subject: { kind: "asset", id: a.data.id, label: a.data.name } } : null);
  const sections = entitySections("asset").filter((s) => !a.data || !s.show || s.show(a.data));
  const types = eventTypesFor("asset");
  if (a.isLoading) return <Loading />;
  if (!a.data) return missing(a.error) ? <NotFound /> : <Page><ErrorNote error={a.error} retry={() => a.refetch()} /></Page>;
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/equipment", label: t("Equipment") }} title={a.data.name} subtitle={[a.data.make, a.data.model, a.data.serial].filter(Boolean).join(" · ")}
        actions={types.length > 0 && <Menu label={t("Add a record")} variant="secondary" items={types.map((e) => ({ label: t(e.label), icon: e.icon, onSelect: () => openRecord({ initial: { type: e.type, subjects: [{ kind: "asset", id: a.data!.id, role: e.subjects.find((s) => s.kind === "asset")!.role, label: a.data!.name }] } }) }))}>{t("Add a record")}</Menu>} />
      {sections.map((s) => <Section key={s.id} title={t(s.label)}><Suspense fallback={<Spinner />}><s.Component entity={a.data!} /></Suspense></Section>)}
      <Section title={t("History")}><Timeline subject={`asset:${a.data.id}`} filters={false} /></Section>
    </Page>
  );
}

export function PeoplePage() {
  const people = useApi<{ items: ContactSummary[] }>("/contacts");
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("People and businesses")} actions={<Button icon="plus" onClick={() => openEntity({ kind: "contact" })}>{t("Add contact")}</Button>} />
      {people.isLoading ? <Loading /> : !people.data?.items.length ? <Empty icon="contact" title={t("No contacts yet")} /> : (
        <List>{people.data.items.map((p) => <Row key={p.id} icon="contact" to={`/ranch/people/${p.id}`} title={p.name} meta={[p.roles.map((r) => t(CONTACT_ROLES[r as keyof typeof CONTACT_ROLES] ?? r)).join(", "), p.phone].filter(Boolean).join(" · ")} />)}</List>
      )}
    </Page>
  );
}

export function ContactPage() {
  const { id } = useParams();
  const c = useApi<ContactSummary & { address: string | null; notes: string | null }>(`/contacts/${id}`);
  useCaptureSubject(c.data ? { subject: { kind: "contact", id: c.data.id, label: c.data.name } } : null);
  const sections = entitySections("contact").filter((s) => !c.data || !s.show || s.show(c.data));
  if (c.isLoading) return <Loading />;
  if (!c.data) return missing(c.error) ? <NotFound /> : <Page><ErrorNote error={c.error} retry={() => c.refetch()} /></Page>;
  return (
    <Page>
      <PageHeader back={{ to: "/ranch/people", label: t("People and businesses") }} title={c.data.name} subtitle={c.data.roles.map((r) => t(CONTACT_ROLES[r as keyof typeof CONTACT_ROLES] ?? r)).join(", ")}
        actions={<Button icon="edit" onClick={() => openEntity({ kind: "contact", id: c.data!.id, initial: { name: c.data!.name, roles: c.data!.roles, phone: c.data!.phone ?? undefined, email: c.data!.email ?? undefined } })}>{t("Edit")}</Button>} />
      <Card>
        <dl className="data-list">
          {c.data.phone && <div><dt>{t("Phone")}</dt><dd><a href={`tel:${c.data.phone}`}>{c.data.phone}</a></dd></div>}
          {c.data.email && <div><dt>{t("Email")}</dt><dd><a href={`mailto:${c.data.email}`}>{c.data.email}</a></dd></div>}
          {c.data.address && <div><dt>{t("Address")}</dt><dd>{c.data.address}</dd></div>}
          {c.data.notes && <div><dt>{t("Notes")}</dt><dd>{c.data.notes}</dd></div>}
        </dl>
      </Card>
      {sections.map((s) => <Section key={s.id} title={t(s.label)}><Suspense fallback={<Spinner />}><s.Component entity={c.data!} /></Suspense></Section>)}
      <Section title={t("History")}><Timeline subject={`contact:${c.data.id}`} filters={false} /></Section>
    </Page>
  );
}
